import Heading from "../ui/Heading";
import Row from "../ui/Row";

import { useSessionContext } from "supertokens-auth-react/recipe/session";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

import { toPascalCase } from "../utils/helpers";
import { Button } from "../components/ui/button";
import ManageBillingButton from "../features/settings/ManageBillingButton";
import CancelSubscriptionButton from "@/features/settings/CancelSubscriptionButton";
import { useUserStore } from "@/stores/UserStore";
import { useGetCancellationDate } from "@/features/settings/useGetCancellationDate";
import RenewSubscriptionButton from "@/features/settings/RenewSubscriptionButton";
import { Skeleton } from "@/components/ui/skeleton";

function SettingsRow({ children, header, description }) {
  return (
    <div className="grid grid-cols-[1fr_200px] items-center space-x-4 rounded-md border p-4">
      <div className="flex-1 space-y-1">
        <p className="text-sm font-medium leading-none">{header}</p>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {children}
    </div>
  );
}

function SubscriptionButton({ cancellationDate, isLoading }) {
  if (isLoading) {
    return <Skeleton className="h-8 w-full" />;
  }

  return cancellationDate ? (
    <RenewSubscriptionButton />
  ) : (
    <CancelSubscriptionButton />
  );
}

function Settings() {
  const session = useSessionContext();
  const currentPlan =
    session?.accessTokenPayload["st-role"]?.["v"]?.[0] || "Unavailable";
  const { data: cancellationDate, isLoading } = useGetCancellationDate();
  return (
    <>
      <Row type="horizontal">
        <Heading as="h1">Settings</Heading>
      </Row>

      <Card>
        <CardContent className="mt-8 grid columns-2 gap-4">
          <p className="text-md leading-none flex items-center space-x-2">
            <div>Plan {"  "}</div>
            <Badge variant="secondary" className="text-sm">
              {toPascalCase(currentPlan)}
            </Badge>
            {cancellationDate && (
              <Badge variant="destructive" className="text-sm">
                Cancels on {" "}
                {new Intl.DateTimeFormat("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "2-digit",
                }).format(new Date(cancellationDate * 1000))}
              </Badge>
            )}
          </p>
          <SettingsRow
            header="Subscription"
            description="View your invoices and manage your current subscription."
          >
            <div className="flex flex-col gap-2">
              <ManageBillingButton />
              <SubscriptionButton
                cancellationDate={cancellationDate}
                isLoading={isLoading}
              />
            </div>
          </SettingsRow>

          <SettingsRow
            header="Onboarding"
            description="Go through the onboarding tutorial again across the whole
                system."
          >
            <Button
              variant="secondary"
              onClick={() =>
                useUserStore.setState({
                  onboardingPath: [
                    "/search",
                    "/leads",
                    "/proposals",
                    "/contract/:proposal_id",
                    "/contract/:proposal_id/generate",
                    "/contract/:proposal_id/generate/editor",
                    "/generate/:proposal_id/editor",
                    "/business_profile",
                    "/schedule",
                    "/generate",
                  ],
                })
              }
            >
              Reset onboarding
            </Button>
          </SettingsRow>
        </CardContent>
      </Card>
    </>
  );
}

export default Settings;
